import { NavLink } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';
import { ROLE_LABELS, can } from '@aisolution/shared';
import { ChangePassword } from './ChangePassword';
import { useLang, LANGUAGES } from '../i18n/lang';
import { G } from '../i18n/glossary';

export function Layout({ children }: { children: ReactNode }) {
  const { user, setUser } = useAuth();
  const { lang, setLang, t } = useLang();
  const [showPw, setShowPw] = useState(false);

  async function logout() {
    await api.logout().catch(() => {});
    setUser(null);
  }

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <img src="/logo-white.png" alt="The AI Solution for 3D" className="brand-logo" />
        </div>
        <nav className="nav">
          {/* "Dashboard", "Bid", "FA Design-Build", "Codes" etc. are trade/product terms — kept English in both languages. */}
          <NavLink to="/" end>{t({ en: 'Dashboard', fil: 'Dashboard' })}</NavLink>
          <NavLink to="/new">{t({ en: '+ New Bid', fil: '+ Bagong Bid' })}</NavLink>
          {user && can(user.role, 'estimate.view') && <NavLink to="/analytics">📊 {t({ en: 'Win/Loss Analytics', fil: 'Win/Loss Analytics' })}</NavLink>}
          <div className="phase-tag">{t({ en: 'Tools', fil: 'Mga Tool' })}</div>
          <NavLink to="/fire-alarm-design">🔥 FA Design-Build</NavLink>
          <NavLink to="/codes">{t({ en: 'Codes & Standards', fil: 'Codes at Standards' })}</NavLink>
          <div className="phase-tag">{t({ en: 'Help', fil: 'Tulong' })}</div>
          <NavLink to="/guide" end>📖 User Guide (English)</NavLink>
          <NavLink to="/guide/fil">📖 Gabay (Filipino)</NavLink>
          {user?.role === 'admin' && (<><div className="phase-tag">Admin</div><NavLink to="/admin">{t({ en: 'Admin Settings', fil: 'Mga Setting ng Admin' })}</NavLink></>)}
        </nav>
      </aside>
      <div className="main">
        <header className="topbar">
          <div className="muted" style={{ fontSize: 13 }}>
            Low-Voltage Division 27/28 · {t({ en: 'AI reads, humans count', fil: 'Nagbabasa ang AI, nagbibilang ang tao' })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Language switch */}
            <div className="lang-switch" role="group" aria-label="Language" style={{ display: 'flex', gap: 4 }}>
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  className={`btn btn-sm ${lang === l.code ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setLang(l.code)}
                  aria-pressed={lang === l.code}
                  title={l.label}
                >{l.short}</button>
              ))}
            </div>
            <span className="who">{user?.name} · {user ? ROLE_LABELS[user.role] : ''}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowPw(true)}>{t(G.changePassword)}</button>
            <button className="btn btn-ghost" onClick={logout}>{t(G.signOut)}</button>
          </div>
        </header>
        <main className="content">{children}</main>
      </div>
      {showPw && <ChangePassword onClose={() => setShowPw(false)} />}
    </div>
  );
}
